import { isCognitoApiTokenEnabled } from "@src/appV2/Auth/cognito/cognitoApiTokenState";
import { getCognitoIdToken } from "@src/appV2/Auth/cognito/cognitoAuth";
import { isFirebaseSessionFallbackEnabled } from "@src/appV2/Auth/cognito/firebaseSessionFallbackState";
import { updateClockOffset } from "@src/appV2/lib/clockOffset";
import axios, { type CustomParamsSerializer } from "axios";
import type * as z from "zod";

import { FirebaseAuth } from "../../utils/firebase";
import {
  environmentConfig,
  isDevelopmentNodeEnvironment,
  isProductionNodeEnvironment,
} from "../environment";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../lib/analytics";

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

interface SchemaValidationErrorContext {
  url: string;
  method: string;
  queryParams?: Record<string, unknown>;
}

/**
 * Error thrown when API response validation fails when parsing schema.
 * Contains the Zod error for programmatic access to validation issues.
 */
export class SchemaValidationError extends Error {
  readonly zodError: z.ZodError;
  readonly context: SchemaValidationErrorContext;

  constructor(zodError: z.ZodError, context: SchemaValidationErrorContext) {
    const issues = zodError.issues
      .map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    super(`Schema validation failed for ${context.method} ${context.url}:\n${issues}`);

    this.name = "SchemaValidationError";
    this.zodError = zodError;
    this.context = context;
  }
}

export interface CommonApiParams<RequestSchema, ResponseSchema> {
  url: string;
  requestSchema?: z.ZodType<RequestSchema>;
  responseSchema: z.ZodType<ResponseSchema>;
  queryParams?: Record<string, unknown>;
  customHeaders?: Record<string, string>;
  paramsSerializer?: CustomParamsSerializer;
}

interface ApiParams<RequestSchema, ResponseSchema> extends CommonApiParams<
  RequestSchema,
  ResponseSchema
> {
  data?: RequestSchema;
  customHeaders?: Record<string, string>;
}

export const apiClient = axios.create({
  baseURL: environmentConfig.REACT_APP_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    // If a custom Authorization header was explicitly provided (e.g. QR code
    // sign-out flow embeds the worker's token), respect it and skip auto-injection.
    if (config.headers.Authorization) {
      return config;
    }

    if (isCognitoApiTokenEnabled() || !isFirebaseSessionFallbackEnabled()) {
      try {
        const cognitoToken = await getCognitoIdToken();
        if (cognitoToken) {
          config.headers.Authorization = `Bearer ${cognitoToken}`;
          return {
            ...config,
            headers: config.headers,
          };
        }
      } catch (error: unknown) {
        logError(APP_V2_APP_EVENTS.GET_COGNITO_TOKEN_FAILURE, {
          error,
          metadata: { source: "axios-interceptor" },
        });
        // Continue to Firebase fallback path below.
      }
    }

    if (!FirebaseAuth.currentUser) {
      return config;
    }

    const authHeader = await FirebaseAuth.currentUser.getIdToken();

    config.headers.Authorization = authHeader;

    return {
      ...config,
      headers: config.headers,
    };
  },
  async (error) => {
    if (error instanceof Error) {
      throw error;
    }
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const serverDate = response.headers?.date;
    if (typeof serverDate === "string") {
      updateClockOffset(serverDate);
    }

    return response;
  },
  async (error) => {
    if (error instanceof Error) {
      throw error;
    }
  }
);
/**
 * @deprecated Use `get` from `./parsedApi` instead
 */
export async function get<RequestSchema, ResponseSchema>({
  url,
  queryParams,
  customHeaders,
  requestSchema,
  responseSchema,
  paramsSerializer,
}: CommonApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(queryParams);
  }

  const response = await apiClient.get<ResponseSchema>(url, {
    params: queryParams,
    headers: customHeaders,
    paramsSerializer,
  });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "GET", parseSchema: false },
      });
    }

    if (isDevelopmentNodeEnvironment()) {
      throw new SchemaValidationError(result.error, { url, method: "GET", queryParams });
    }

    return response;
  }

  return response;
}

/**
 * @deprecated Use `post` from `./parsedApi` instead
 */
export async function post<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  customHeaders,
  requestSchema,
  responseSchema,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.post<ResponseSchema>(url, data, {
    params: queryParams,
    headers: customHeaders,
  });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "POST", queryParams, parseSchema: false },
      });
    }

    if (isDevelopmentNodeEnvironment()) {
      throw new SchemaValidationError(result.error, { url, method: "POST", queryParams });
    }
  }

  return response;
}

/**
 * @deprecated Use `put` from `./parsedApi` instead
 */
export async function put<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  requestSchema,
  responseSchema,
  customHeaders,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.put<ResponseSchema>(url, data, {
    params: queryParams,
    headers: customHeaders,
  });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "PUT", queryParams, parseSchema: false },
      });
    }

    if (isDevelopmentNodeEnvironment()) {
      throw new SchemaValidationError(result.error, { url, method: "PUT", queryParams });
    }
  }

  return response;
}

/**
 * @deprecated Use `patch` from `./parsedApi` instead
 */
export async function patch<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  requestSchema,
  responseSchema,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.patch<ResponseSchema>(url, data, { params: queryParams });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "PATCH", queryParams, parseSchema: false },
      });
    }

    if (isDevelopmentNodeEnvironment()) {
      throw new SchemaValidationError(result.error, { url, method: "PATCH", queryParams });
    }
  }

  return response;
}

/**
 * @deprecated Use `remove` from `./parsedApi` instead
 */
export async function remove<RequestSchema, ResponseSchema>({
  url,
  data,
  requestSchema,
  responseSchema,
  queryParams,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.delete<ResponseSchema>(url, { params: queryParams });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "DELETE", parseSchema: false },
      });
    }

    if (isDevelopmentNodeEnvironment()) {
      throw new SchemaValidationError(result.error, { url, method: "DELETE", queryParams });
    }
  }

  return response;
}
